import { PlusCircleFilled } from "@ant-design/icons";
import { AssetsHost, COLORS } from "@coeff/utils";
import React, { MouseEventHandler, ReactNode, useContext } from "react";
import styled from "styled-components";

import { DEPRECATED_Button as CoeffButton, ButtonProps } from "../../common/DEPRECATED_Button";
import { DotsLoader } from "../../common/DotsLoader";
import { PpressContext, PPressContextParams, PPressComponentProps } from "../context";

export const StyledButton = styled(CoeffButton)`
  && {
    width: 100%;
    margin-bottom: 10px;
    min-height: 32px;
    height: auto;

    &.reset-styles {
      width: auto;
      height: auto;
      margin: auto;
      padding: 0px;
      line-height: inherit;
      min-height: auto;
      background: none;
      border: none;
      // Match the weight of sibling link-style controls like PivotV2's "+ add".
      font-weight: 600;

      span {
        margin: auto;
      }
    }

    &.text-left {
      text-align: left;

      span:not(.anticon) {
        white-space: normal;
        overflow-wrap: anywhere;
      }
    }

    &.text-large {
      font-size: 16px;
      font-weight: 500;
    }

    &.no-pad {
      padding: 0px 8px;
      margin-bottom: 2px;
    }

    &.white-background {
      background-color: ${COLORS.white};
    }

    // For Quickbooks OAuth button. It is a requirement from QuickBooks team.
    &.green-background {
      background: url("${AssetsHost()}/C2QB_green_btn_med_default_2x.png") no-repeat;
      &:hover {
        background: url("${AssetsHost()}/C2QB_green_btn_med_hover_2x.png") no-repeat;
      }

      &::after {
        all: unset;
      }

      border-radius: unset !important;
      background-size: contain !important;
      box-shadow: unset !important;
      background-color: transparent !important;
      border-color: transparent !important;
      height: 36px !important;
      background-position: center !important;
      span {
        display: none;
      }
    }

    &.large {
      line-height: 28px;
      font-size: 18px;
      height: 48px;

      .anticon {
        font-size: 24px;
      }

      span:not(.anticon) {
        margin-left: 13px;
        position: relative;
        bottom: 2px;
      }
    }

    &.no-shadow {
      box-shadow: none;
    }

    &.bg-gray {
      background-color: ${COLORS.black4};
      border-color: ${COLORS.black4};

      &:hover {
        background-color: ${COLORS.white};
        border-color: ${COLORS.black4};
        color: inherit;
      }

      .anticon {
        color: ${COLORS.coeblue4}!important;
        font-size: 16px;
        vertical-align: top;
        position: relative;
        top: 3px;
      }
      span:not(.anticon) {
        white-space: normal;
      }
    }

    &.bg-coeblue {
      background-color: ${COLORS.coeblue};
      border: none;
      color: ${COLORS.black85};

      &:hover {
        background-color: #cce7ff;
        color: ${COLORS.black85};
      }

      .anticon {
        color: ${COLORS.coeblue4}!important;
      }
    }

    &.bg-purple {
      background-color: ${COLORS.purpleBg};
      border: none;
      color: ${COLORS.black85};

      &:hover {
        background-color: ${COLORS.purpleBg2};
        color: ${COLORS.black85};
      }

      .anticon {
        color: ${COLORS.purple}!important;
      }
    }

    &.light {
      color: ${COLORS.coeblue4};
      background: ${COLORS.coeblue};
      border-color: ${COLORS.coeblue};

      &:focus {
        border-color: ${COLORS.coeblue};
        box-shadow: none;
      }

      &:hover {
        border-color: ${COLORS.coeblue};
      }
    }

    &.link {
      border: none;
      box-shadow: none;
      color: ${COLORS.coeblue4};
      background: none;

      &:hover {
        border: none;
        color: ${COLORS.coeblue4};
      }
    }
  }
`;

type PPressOAuth2ButtonProps = {
  buttonText: string;
  authClickHandler: () => void;
  authorizationUrl: string;
  className?: string;
} & ButtonProps;

export const OAuth2Button = (props: PPressOAuth2ButtonProps) => {
  const ppressContext = useContext(PpressContext);

  const { oauthInProgress, setOauthInProgress } = ppressContext ?? {};

  return (
    <div>
      <StyledButton
        type="primary"
        disabled={oauthInProgress || props.disabled}
        className={props.className}
        onClick={() => props.authClickHandler()}
      >
        {oauthInProgress ? (
          <DotsLoader color="white" style={{ margin: "auto" }} />
        ) : (
          props.buttonText
        )}
      </StyledButton>

      {oauthInProgress && setOauthInProgress ? (
        <StyledButton className="link" type="link" onClick={() => setOauthInProgress(false)}>
          Cancel
        </StyledButton>
      ) : null}
    </div>
  );
};

type PPressAPIKeyAuthButtonProps<T = Record<string, string>> = {
  buttonText: string;
  authClickHandler: (metadata: T) => void;
  ppressMetadata: T;
} & ButtonProps;

export const APIKeyAuthButton = (props: PPressAPIKeyAuthButtonProps) => {
  const ppressContext = useContext(PpressContext);

  const { oauthInProgress, setOauthInProgress } = ppressContext ?? {};

  return (
    <div>
      <StyledButton
        type="primary"
        disabled={oauthInProgress || props.disabled}
        className={props.className}
        onClick={() => {
          props.authClickHandler(props.ppressMetadata ?? {});
        }}
      >
        {oauthInProgress ? (
          <DotsLoader color="white" style={{ margin: "auto" }} />
        ) : (
          props.buttonText
        )}
      </StyledButton>

      {oauthInProgress && setOauthInProgress ? (
        <StyledButton className="link" type="link" onClick={() => setOauthInProgress(false)}>
          Cancel
        </StyledButton>
      ) : null}
    </div>
  );
};

type PPressButtonProps = {
  label: string;
  type?: "primary" | "default" | "link";
  className?: string;
  icon?: string;
  disabled?: boolean;
  children?: ReactNode;
} & PPressComponentProps;

const ButtonIcon = (iconName?: string): ReactNode => {
  switch (iconName) {
    case "plus":
      return <PlusCircleFilled />;
    default:
      return;
  }
};

export const Button = (props: PPressButtonProps) => {
  const form_state = useContext(PpressContext) as PPressContextParams;
  return (
    <StyledButton
      className={props.className}
      icon={ButtonIcon(props.icon)}
      onClick={() => form_state.updateFormFromServer(form_state.ppress_params)}
      type={props.type}
      disabled={props.disabled}
    >
      {props.children ?? props.label}
    </StyledButton>
  );
};

// <SetParamButton> is a <Button> that sets a param when clicked
// eg. These two calls are equivalent:
// <SetParamButton name="fetch_mode" value="issues" updateFromServerOnChange />
// <SetParamButton value={{"fetch_mode": "issues"}} updateFromServerOnChange />
// 2nd form is useful for updating multiple keys.

type PPressSetParamButtonProps = {
  name?: string;
  value?: any;
  style?: object;
  disabled?: boolean;
  onBeforeUpdate?: () => void;
} & PPressButtonProps;

export const SetParamButton = (props: PPressSetParamButtonProps) => {
  const form_state = useContext(PpressContext) as PPressContextParams;

  const { currentPreviewModalTrigger } = form_state;

  const { triggersOnChange } = props;

  const onClick: MouseEventHandler = async e => {
    let updaterObject;

    if (props.name) {
      // if name prop, update single key in ppress_params
      updaterObject = { [props.name]: props.value };
    } else {
      updaterObject = { ...(props.value as object) };
    }

    props.onBeforeUpdate?.();

    form_state.updatePPressParams(updaterObject);

    if (triggersOnChange) {
      form_state.triggersOnChange(triggersOnChange);
    }

    if (props.updateFromServerOnChange) {
      await form_state.updateFormFromServer({
        ...form_state.ppress_params,
        ...updaterObject,
      });
    }
  };

  return (
    <StyledButton
      disabled={form_state.is_loading || props.disabled || currentPreviewModalTrigger !== undefined}
      className={"coeff__ppress_button " + props.className}
      icon={ButtonIcon(props.icon)}
      onClick={onClick}
      type={props.type}
      style={props.style}
    >
      {props.label}
    </StyledButton>
  );
};

type PPressOpenUrlButtonProps = {
  url: string;
} & PPressButtonProps;

export const OpenUrlButton = (props: PPressOpenUrlButtonProps) => {
  return (
    <StyledButton
      className={props.className}
      type={props.type}
      disabled={props.disabled}
      onClick={() => {
        window.open(props.url, "_blank");
      }}
    >
      {props.label}
    </StyledButton>
  );
};
